import { useState, useEffect } from 'react'
import { useReportStore } from '@/store/report-store'
import { useAuthStore } from '@/store/auth-store'
import api from '@/services/api'

interface Props {
  onApply: () => void
  reportId: string
}

interface UserOption { id: string; displayName: string }
interface ClientOption { id: string; name: string }

const PROJECT_TYPES = [
  { value: 'asphalt', label: 'Asphalt' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'grading', label: 'Grading' },
  { value: 'seal-coat', label: 'Seal Coat' },
  { value: 'striping', label: 'Striping' },
]

const DATE_PRESETS = [
  { label: 'Last 30 Days', getValue: () => { const d = new Date(); d.setDate(d.getDate() - 30); return d } },
  { label: 'Last 90 Days', getValue: () => { const d = new Date(); d.setDate(d.getDate() - 90); return d } },
  { label: 'This Quarter', getValue: () => { const now = new Date(); return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1) } },
  { label: 'YTD', getValue: () => new Date(new Date().getFullYear(), 0, 1) },
  { label: 'Last Year', getValue: () => new Date(new Date().getFullYear() - 1, 0, 1) },
]

function toDateStr(d: Date) {
  return d.toISOString().split('T')[0]
}

export function ReportFilters({ onApply, reportId }: Props) {
  const user = useAuthStore((s) => s.user)
  const userRole = user?.role || ''
  const isManagerOrAbove = userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERADMIN'

  const { filters, setFilter, setFilters, resetFilters, loadCacheForReport, saveCacheForReport } = useReportStore()
  const [users, setUsers] = useState<UserOption[]>([])
  const [clients, setClients] = useState<ClientOption[]>([])

  // Load cached filters for this report on mount
  useEffect(() => {
    loadCacheForReport(reportId)
  }, [reportId, loadCacheForReport])

  // Load dropdown options
  useEffect(() => {
    if (isManagerOrAbove) {
      api.get('/users').then(({ data }) => {
        const list = (data.data?.users || data.data || []) as UserOption[]
        setUsers(list.filter((u: any) => u.role === 'ESTIMATOR' || u.role === 'MANAGER'))
      }).catch(() => {})
    }
    api.get('/clients').then(({ data }) => {
      setClients((data.data?.clients || data.data || []) as ClientOption[])
    }).catch(() => {})
  }, [isManagerOrAbove])

  function handleApply() {
    saveCacheForReport(reportId)
    onApply()
  }

  function handleReset() {
    resetFilters()
    onApply()
  }

  function handlePreset(preset: typeof DATE_PRESETS[0]) {
    const start = preset.getValue()
    setFilters({
      dateStart: toDateStr(start),
      dateEnd: toDateStr(new Date()),
    })
  }

  function toggleProjectType(type: string) {
    const current = filters.projectTypes || []
    const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type]
    setFilter('projectTypes', next)
  }

  return (
    <aside className="rep-filters">
      <div className="rep-filters-body">
        {/* Date Range */}
          <div className="rep-filters-group">
            <label className="rep-filters-label">Date Range</label>
            <div className="rep-filters-presets">
              {DATE_PRESETS.map((p) => (
                <button key={p.label} type="button" className="rep-preset-btn"
                  onClick={() => handlePreset(p)}>{p.label}</button>
              ))}
            </div>
            <div className="rep-filters-date-stack">
              <label className="rep-filters-date-label">From</label>
              <input type="date" className="rep-filters-input"
                value={filters.dateStart} onChange={(e) => setFilter('dateStart', e.target.value)} />
              <label className="rep-filters-date-label">To</label>
              <input type="date" className="rep-filters-input"
                value={filters.dateEnd} onChange={(e) => setFilter('dateEnd', e.target.value)} />
            </div>
          </div>

          {/* Project Types */}
          <div className="rep-filters-group">
            <label className="rep-filters-label">Project Types</label>
            <div className="rep-filters-checks">
              {PROJECT_TYPES.map((pt) => (
                <label key={pt.value} className="rep-check-label">
                  <input type="checkbox" checked={filters.projectTypes.includes(pt.value)}
                    onChange={() => toggleProjectType(pt.value)} />
                  <span>{pt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Estimator (managers+ only) */}
          {isManagerOrAbove && (
            <div className="rep-filters-group">
              <label className="rep-filters-label">Estimator</label>
              <select className="rep-filters-select"
                value={filters.estimatorId} onChange={(e) => setFilter('estimatorId', e.target.value)}>
                <option value="">All Estimators</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Client */}
          <div className="rep-filters-group">
            <label className="rep-filters-label">Client</label>
            <select className="rep-filters-select"
              value={filters.clientId} onChange={(e) => setFilter('clientId', e.target.value)}>
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Public Works */}
          <div className="rep-filters-group">
            <label className="rep-filters-label">Public Works</label>
            <select className="rep-filters-select"
              value={filters.isPublicWorks} onChange={(e) => setFilter('isPublicWorks', e.target.value)}>
              <option value="">All</option>
              <option value="true">Public Works Only</option>
              <option value="false">Private Only</option>
            </select>
          </div>

          {/* Actions */}
          <div className="rep-filters-actions">
            <button className="rep-apply-btn" onClick={handleApply}>Apply Filters</button>
            <button className="rep-reset-btn" onClick={handleReset}>Reset</button>
          </div>
        </div>
    </aside>
  )
}
